import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

export default function BeforeAfterSlider({ before, after, alt = "Detailing transformation", objectPosition = "center" }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="group relative aspect-[3/4] w-full select-none overflow-hidden rounded-2xl border border-brand-silver/40"
      style={{ touchAction: "none", cursor: "ew-resize" }}
      onPointerDown={(e) => { setDragging(true); move(e.clientX); }}
      onPointerMove={(e) => dragging && move(e.clientX)}
      onPointerUp={() => setDragging(false)}
      onPointerLeave={() => setDragging(false)}
    >
      {/* After (base layer) */}
      <img
        src={after}
        alt={`${alt} — after`}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition }}
      />
      <span className="absolute right-3 top-3 rounded-full bg-brand-charcoal/70 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
        After
      </span>

      {/* Before (clipped overlay) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src={before}
          alt={`${alt} — before`}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition }}
        />
        <span className="absolute left-3 top-3 rounded-full bg-brand-red px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-wider text-white">
          Before
        </span>
      </div>

      {/* Handle */}
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="h-full w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.4)]" />
        <div className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-brand-red text-white shadow-lg transition-transform group-hover:scale-110">
          <MoveHorizontal className="h-5 w-5" />
        </div>
      </div>

      <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-charcoal/60 px-3 py-1 text-[0.65rem] font-medium text-white backdrop-blur-sm opacity-90">
        ← Drag to reveal →
      </span>
    </div>
  );
}
